/**
 * QuestionEditor Component
 *
 * Edits properties of a single question in the form builder.
 * Refined, modern UI with visual selectors and clean typography.
 */

import React, { useState, useCallback, useRef } from 'react'
import { COUNTRY_CODES } from '../../constants/countryCodes'
import { RATING_STYLE_OPTIONS, getRatingPreset } from '../../constants/ratingPresets'
import * as mediaApi from '../../api/media'

// Curated colour palette for question backgrounds
const COLOR_SWATCHES = [
  { label: 'Default', value: '' },
  { label: 'Ice Blue', value: '#eff6ff' },
  { label: 'Lavender', value: '#f3f0ff' },
  { label: 'Mint', value: '#ecfdf5' },
  { label: 'Peach', value: '#fff7ed' },
  { label: 'Rose', value: '#fff1f2' },
  { label: 'Lemon', value: '#fefce8' },
  { label: 'Slate', value: '#f1f5f9' },
  { label: 'Lilac', value: '#faf5ff' },
]

export default function QuestionEditor({ question, onUpdate, surveyId }) {
  const [showCustomColor, setShowCustomColor] = useState(false)
  const colorRafRef = useRef(null)

  const handleChange = (field, value) => {
    onUpdate({ [field]: value })
  }

  // Throttled handler for the native <input type="color"> to prevent
  // rapid state updates while the user drags the colour picker.
  const handleColorPickerChange = useCallback((e) => {
    const val = e.target.value
    if (colorRafRef.current) cancelAnimationFrame(colorRafRef.current)
    colorRafRef.current = requestAnimationFrame(() => {
      handleChange('backgroundColor', val)
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOptionChange = (index, value) => {
    const newOptions = [...(question.options || [])]
    newOptions[index] = value
    onUpdate({ options: newOptions })
  }

  const addOption = () => {
    const newOptions = [...(question.options || []), '']
    onUpdate({ options: newOptions })
  }

  const removeOption = (index) => {
    const newOptions = question.options.filter((_, i) => i !== index)
    onUpdate({ options: newOptions })
  }

  const TYPE_META = {
    text: { label: 'Text Input', icon: '📝' },
    textarea: { label: 'Long Text', icon: '📄' },
    email: { label: 'Email', icon: '📧' },
    number: { label: 'Number', icon: '🔢' },
    rating: { label: 'Rating', icon: '⭐' },
    select: { label: 'Dropdown', icon: '📋' },
    radio: { label: 'Single Choice', icon: '🔘' },
    checkbox: { label: 'Multiple Choice', icon: '☑️' },
    phone: { label: 'Phone Number', icon: '📱' },
    audio: { label: 'Audio Recording', icon: '🎤' },
    video: { label: 'Video Recording', icon: '📹' },
    image: { label: 'Image/Photo', icon: '📷' },
    open_response: { label: 'Open Response', icon: '💬' },
    section_header: { label: 'Section Header', icon: '📌' },
    divider: { label: 'Divider', icon: '➖' },
  }

  const meta = TYPE_META[question.type] || { label: question.type, icon: '❓' }
  const isLayout = ['section_header', 'divider'].includes(question.type)

  // Convert milliseconds to minutes for display
  const msToMinutes = (ms) => Math.round((ms || 0) / 60000)
  const minutesToMs = (min) => (min || 0) * 60000

  // Active background swatch
  const currentBg = question.backgroundColor || ''
  const isCustomBg = currentBg && !COLOR_SWATCHES.some((s) => s.value === currentBg)

  return (
    <div className="question-editor">
      {/* Header */}
      <div className="editor-header">
        <div className="editor-header-left">
          <span className="editor-type-icon">{meta.icon}</span>
          <div>
            <h3>Edit Question</h3>
            <span className="editor-type-subtitle">{meta.label}</span>
          </div>
        </div>
      </div>

      <div className="editor-form">
        {/* ─── Divider: minimal ─── */}
        {question.type === 'divider' && (
          <div className="editor-card editor-card-muted">
            <p className="editor-card-note">
              This is a visual separator between question groups. No configuration needed.
            </p>
          </div>
        )}

        {/* ─── Label ─── */}
        {question.type !== 'divider' && (
          <div className="editor-card">
            <label className="editor-field-label" htmlFor="q-label">
              {question.type === 'section_header' ? 'Section Title' : 'Question Text'}
            </label>
            <input
              id="q-label"
              type="text"
              className="editor-field-input"
              value={question.label || ''}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder={
                question.type === 'section_header' ? 'Enter section title' : 'Enter your question'
              }
            />
          </div>
        )}

        {/* ─── Section header subtitle ─── */}
        {question.type === 'section_header' && (
          <div className="editor-card">
            <label className="editor-field-label" htmlFor="q-description">
              Subtitle
            </label>
            <input
              id="q-description"
              type="text"
              className="editor-field-input"
              value={question.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief description for this section (optional)"
            />
          </div>
        )}

        {/* ─── Required toggle ─── */}
        {!isLayout && (
          <div className="editor-card editor-card-row">
            <div className="editor-toggle-info">
              <span className="editor-toggle-label">Required</span>
              <span className="editor-toggle-hint">Respondent must answer this question</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={question.required || false}
                onChange={(e) => handleChange('required', e.target.checked)}
              />
              <span className="toggle-track"></span>
            </label>
          </div>
        )}

        {/* ─── Placeholder ─── */}
        {!isLayout && ['text', 'textarea', 'email', 'number', 'phone'].includes(question.type) && (
          <div className="editor-card">
            <label className="editor-field-label" htmlFor="q-placeholder">
              Placeholder
            </label>
            <input
              id="q-placeholder"
              type="text"
              className="editor-field-input editor-field-input-sm"
              value={question.placeholder || ''}
              onChange={(e) => handleChange('placeholder', e.target.value)}
              placeholder="Placeholder text shown to respondent"
            />
          </div>
        )}

        {/* ─── Textarea rows ─── */}
        {question.type === 'textarea' && (
          <div className="editor-card">
            <label className="editor-field-label" htmlFor="q-rows">
              Number of Rows
            </label>
            <input
              id="q-rows"
              type="number"
              className="editor-field-input editor-field-input-sm"
              min="2"
              max="20"
              value={question.rows || 4}
              onChange={(e) => handleChange('rows', parseInt(e.target.value) || 4)}
            />
          </div>
        )}

        {/* ─── Number min/max ─── */}
        {question.type === 'number' && (
          <div className="editor-card">
            <span className="editor-field-label">Value Range</span>
            <div className="editor-inline-fields">
              <div className="editor-inline-field">
                <label className="editor-field-sublabel" htmlFor="q-min">
                  Min
                </label>
                <input
                  id="q-min"
                  type="number"
                  className="editor-field-input editor-field-input-sm"
                  value={question.min ?? ''}
                  onChange={(e) =>
                    handleChange('min', e.target.value === '' ? null : parseInt(e.target.value))
                  }
                  placeholder="—"
                />
              </div>
              <span className="editor-inline-divider">to</span>
              <div className="editor-inline-field">
                <label className="editor-field-sublabel" htmlFor="q-max">
                  Max
                </label>
                <input
                  id="q-max"
                  type="number"
                  className="editor-field-input editor-field-input-sm"
                  value={question.max ?? ''}
                  onChange={(e) =>
                    handleChange('max', e.target.value === '' ? null : parseInt(e.target.value))
                  }
                  placeholder="—"
                />
              </div>
            </div>
          </div>
        )}

        {/* ─── Phone default country ─── */}
        {question.type === 'phone' && (
          <div className="editor-card">
            <label className="editor-field-label" htmlFor="q-country-code">
              Default Country Code
            </label>
            <select
              id="q-country-code"
              className="editor-field-select"
              value={question.countryCode || '+91'}
              onChange={(e) => handleChange('countryCode', e.target.value)}>
              {COUNTRY_CODES.map((c) => (
                <option key={`${c.code}-${c.dialCode}`} value={c.dialCode}>
                  {c.name} ({c.dialCode})
                </option>
              ))}
            </select>
            <span className="editor-field-hint">
              Pre-selected for respondents, auto-detected from their location
            </span>
          </div>
        )}

        {/* ─── Rating settings ─── */}
        {question.type === 'rating' && (
          <div className="editor-card">
            <span className="editor-field-label">Rating Scale</span>

            {/* Max levels as pill toggles */}
            <div className="editor-pill-group">
              {[3, 5, 7, 10].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`editor-pill ${(question.maxRating || 5) === n ? 'active' : ''}`}
                  onClick={() => handleChange('maxRating', n)}>
                  {n}
                </button>
              ))}
              <span className="editor-pill-label">levels</span>
            </div>

            {/* Rating style as visual tiles */}
            <span className="editor-field-label" style={{ marginTop: '1rem' }}>
              Style
            </span>
            <div className="rating-tile-grid">
              {RATING_STYLE_OPTIONS.map((opt) => {
                const isActive = (question.ratingStyle || 'stars') === opt.value
                const preset = opt.value !== 'custom' ? getRatingPreset(opt.value) : null
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`rating-tile ${isActive ? 'active' : ''}`}
                    onClick={() => handleChange('ratingStyle', opt.value)}>
                    <span className="rating-tile-preview">
                      {preset
                        ? Array.from({ length: 3 }, (_, i) => (
                            <span key={i} style={{ color: preset.getColor(i, 3) }}>
                              {preset.getIcon(i, 3, true)}
                            </span>
                          ))
                        : '🖼️'}
                    </span>
                    <span className="rating-tile-name">{opt.label.replace(/ .*/, '')}</span>
                  </button>
                )
              })}
            </div>

            {/* Live preview at full scale */}
            {(question.ratingStyle || 'stars') !== 'custom' && (
              <div className="rating-live-preview">
                <span
                  className="editor-field-hint"
                  style={{ marginBottom: '0.4rem', display: 'block' }}>
                  Preview
                </span>
                <div className="rating-live-icons">
                  {Array.from({ length: question.maxRating || 5 }, (_, i) => {
                    const preset = getRatingPreset(question.ratingStyle || 'stars')
                    const maxR = question.maxRating || 5
                    return (
                      <span
                        key={i}
                        className="rating-live-icon"
                        style={{ color: preset.getColor(i, maxR) }}>
                        {preset.getIcon(i, maxR, true)}
                      </span>
                    )
                  })}
                </div>
              </div>
            )}

            {/* ─── Custom image uploads ─── */}
            {question.ratingStyle === 'custom' && (
              <div className="custom-icons-section">
                <span className="editor-field-hint">
                  Upload one image per rating level. Unselected icons appear dimmed with grayscale.
                </span>
                <div className="custom-rating-grid">
                  {Array.from({ length: question.maxRating || 5 }, (_, i) => {
                    const icons = question.customRatingIcons || []
                    const iconUrl = icons[i] || null
                    return (
                      <div key={i} className="custom-icon-cell">
                        {iconUrl ? (
                          <div className="custom-icon-filled">
                            <img src={iconUrl} alt={`Rating ${i + 1}`} />
                            <button
                              type="button"
                              className="custom-icon-remove-btn"
                              onClick={() => {
                                const newIcons = [...(question.customRatingIcons || [])]
                                newIcons[i] = null
                                onUpdate({ customRatingIcons: newIcons })
                              }}
                              title="Remove">
                              ✕
                            </button>
                            <span className="custom-icon-num">{i + 1}</span>
                          </div>
                        ) : (
                          <label className="custom-icon-empty">
                            <span className="custom-icon-plus">+</span>
                            <span className="custom-icon-num">{i + 1}</span>
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={async (e) => {
                                const file = e.target.files?.[0]
                                if (!file || !surveyId) return
                                try {
                                  const result = await mediaApi.uploadSurveyBrandImage(
                                    surveyId,
                                    file
                                  )
                                  const newIcons = [
                                    ...(question.customRatingIcons ||
                                      Array(question.maxRating || 5).fill(null)),
                                  ]
                                  newIcons[i] = result.url
                                  onUpdate({ customRatingIcons: newIcons })
                                } catch (err) {
                                  console.error('Failed to upload rating icon:', err)
                                }
                                e.target.value = ''
                              }}
                            />
                          </label>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── Options (select, radio, checkbox) ─── */}
        {['select', 'radio', 'checkbox'].includes(question.type) && (
          <div className="editor-card">
            <span className="editor-field-label">Options</span>
            <div className="options-list">
              {(question.options || []).map((option, index) => (
                <div key={index} className="option-row">
                  <span className="option-drag-handle">⋮⋮</span>
                  <input
                    type="text"
                    className="editor-field-input"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                  />
                  <button
                    type="button"
                    className="option-remove"
                    onClick={() => removeOption(index)}
                    disabled={(question.options?.length || 0) <= 1}
                    title="Remove">
                    ✕
                  </button>
                </div>
              ))}
              <button type="button" className="option-add" onClick={addOption}>
                + Add option
              </button>
            </div>
          </div>
        )}

        {/* ─── Audio duration ─── */}
        {question.type === 'audio' && (
          <div className="editor-card">
            <label className="editor-field-label" htmlFor="q-max-duration">
              Max Recording Duration
            </label>
            <select
              id="q-max-duration"
              className="editor-field-select"
              value={msToMinutes(question.maxDurationMs || 300000)}
              onChange={(e) =>
                handleChange('maxDurationMs', minutesToMs(parseInt(e.target.value)))
              }>
              <option value={1}>1 minute</option>
              <option value={2}>2 minutes</option>
              <option value={5}>5 minutes</option>
              <option value={10}>10 minutes</option>
              <option value={15}>15 minutes</option>
            </select>
          </div>
        )}

        {/* ─── Video duration ─── */}
        {question.type === 'video' && (
          <div className="editor-card">
            <label className="editor-field-label" htmlFor="q-max-duration">
              Max Recording Duration
            </label>
            <select
              id="q-max-duration"
              className="editor-field-select"
              value={msToMinutes(question.maxDurationMs || 120000)}
              onChange={(e) =>
                handleChange('maxDurationMs', minutesToMs(parseInt(e.target.value)))
              }>
              <option value={1}>1 minute</option>
              <option value={2}>2 minutes</option>
              <option value={3}>3 minutes</option>
              <option value={5}>5 minutes</option>
            </select>
          </div>
        )}

        {/* ─── Image upload toggle ─── */}
        {question.type === 'image' && (
          <div className="editor-card editor-card-row">
            <div className="editor-toggle-info">
              <span className="editor-toggle-label">Allow file upload</span>
              <span className="editor-toggle-hint">In addition to camera capture</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={question.allowUpload !== false}
                onChange={(e) => handleChange('allowUpload', e.target.checked)}
              />
              <span className="toggle-track"></span>
            </label>
          </div>
        )}

        {/* ─── Open response hint ─── */}
        {question.type === 'open_response' && (
          <div className="editor-card editor-card-info">
            <span className="editor-card-info-icon">💡</span>
            <p>
              Respondents can choose to answer via <strong>text</strong>,{' '}
              <strong>audio recording</strong>, or <strong>image/photo</strong>. Media response
              types must be enabled in Survey Settings.
            </p>
          </div>
        )}

        {/* ─── Media type hint ─── */}
        {['audio', 'video', 'image'].includes(question.type) && (
          <div className="editor-card editor-card-info">
            <span className="editor-card-info-icon">ℹ️</span>
            <p>
              Media response types must be enabled in Survey Settings for this question to work.
            </p>
          </div>
        )}

        {/* ─── Background colour ─── */}
        {question.type !== 'divider' && (
          <div className="editor-card">
            <span className="editor-field-label">Background Colour</span>
            <span className="editor-field-hint">
              Overrides the global setting for this question
            </span>
            <div className="swatch-grid">
              {COLOR_SWATCHES.map((sw) => (
                <button
                  key={sw.value || 'default'}
                  type="button"
                  className={`swatch-btn ${currentBg === sw.value ? 'active' : ''}`}
                  title={sw.label}
                  onClick={() => handleChange('backgroundColor', sw.value)}>
                  <span className="swatch-circle" style={{ background: sw.value || '#f7fafc' }} />
                  {sw.value === '' && <span className="swatch-slash" />}
                </button>
              ))}
              {/* Custom colour button */}
              <button
                type="button"
                className={`swatch-btn swatch-custom-toggle ${isCustomBg ? 'active' : ''}`}
                title="Custom colour"
                onClick={() => setShowCustomColor(!showCustomColor)}>
                <span className="swatch-circle swatch-rainbow" />
              </button>
            </div>
            {(showCustomColor || isCustomBg) && (
              <div className="custom-color-row">
                <input
                  type="color"
                  className="custom-color-input"
                  value={currentBg || '#f7fafc'}
                  onChange={handleColorPickerChange}
                />
                <input
                  type="text"
                  className="custom-color-hex"
                  value={currentBg || ''}
                  onChange={(e) => {
                    const val = e.target.value
                    if (/^#[0-9a-fA-F]{0,6}$/.test(val) || val === '') {
                      handleChange('backgroundColor', val)
                    }
                  }}
                  placeholder="#hex"
                  maxLength={7}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
